import { useEffect, useMemo, useRef, useState } from "react";
import {
  FileText, FileDown, MessageCircle, Save, Plus, Trash2, RefreshCw,
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, Info, Wand2,
} from "lucide-react";
import { api, formatApiError, isSuperRole } from "../../utils/auth";
import { normalizeWANumberLocal } from "../../utils/fonnte";

const BULAN = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

const LABEL_STATUS = {
  BELUM_MULAI: "belum dimulai",
  ON_PROGRESS: "sedang berjalan",
  SELESAI: "selesai",
  TERHAMBAT: "terhambat",
};

function tanggalHariIni() {
  const d = new Date();
  return `${d.getDate()} ${BULAN[d.getMonth()]} ${d.getFullYear()}`;
}

// Ubah data sub-kegiatan menjadi kalimat progres yang siap disunting.
function ringkasSub(sub) {
  const status = LABEL_STATUS[sub.status] || "belum dimulai";
  const inti = `${sub.nama} — ${status} (${sub.progress ?? 0}%)`;
  const catatan = (sub.catatan || "").trim();
  return catatan ? `${inti}. ${catatan}` : `${inti}.`;
}

// Dikelompokkan per program: satu baris laporan = satu program, isinya ringkasan
// seluruh sub-kegiatan di bawahnya. Bekerja dari daftar sub yang sudah diratakan
// (punya programNama/programKode), sama seperti yang dipakai halaman Progres.
function susunBarisOtomatis(subs) {
  const kelompok = new Map();
  (subs || [])
    .filter((s) => s.specialType !== "AUDIT_INVESTIGASI")
    .forEach((s) => {
      const nama = s.programNama || s.programKode || "Lain-lain";
      if (!kelompok.has(nama)) kelompok.set(nama, []);
      kelompok.get(nama).push(s);
    });

  return [...kelompok.entries()].map(([nama, isi], i) => ({
    no: String(i + 1),
    uraian: nama,
    progres: isi.map(ringkasSub).join("\n"),
  }));
}

function unduhBlob(blob, namaBerkas) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = namaBerkas;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export default function LaporanProgresPanel({ subs, user }) {
  const bolehUbah = isSuperRole(user);
  const [terbuka, setTerbuka] = useState(false);
  const [tanggal, setTanggal] = useState(tanggalHariIni);
  const [baris, setBaris] = useState([]);
  const [lampiran, setLampiran] = useState([]);
  const [sibuk, setSibuk] = useState(null);   // "docx" | "pdf" | "simpan" | "wa"
  const [pesan, setPesan] = useState(null);
  const [error, setError] = useState(null);
  const sudahMuat = useRef(false);

  // Muat naskah tersimpan; kalau belum ada, susun otomatis dari data progres.
  useEffect(() => {
    if (sudahMuat.current) return;
    sudahMuat.current = true;
    api
      .get("/laporan-progres/draft")
      .then(({ data }) => {
        if (data?.ada && data.isi) {
          setTanggal(data.isi.tanggal || tanggalHariIni());
          setBaris(data.isi.baris || []);
          setLampiran(data.isi.lampiran || []);
        } else {
          setBaris(susunBarisOtomatis(subs));
        }
      })
      .catch(() => setBaris(susunBarisOtomatis(subs)));
  }, [subs]);

  const isiLaporan = useMemo(
    () => ({
      tanggal,
      baris: baris.map((b, i) => ({ ...b, no: b.no || String(i + 1) })),
      lampiran,
    }),
    [tanggal, baris, lampiran]
  );

  const jumlahKata = useMemo(
    () => baris.reduce((a, b) => a + (b.progres || "").split(/\s+/).filter(Boolean).length, 0),
    [baris]
  );

  function bersihkanPesan() {
    setPesan(null);
    setError(null);
  }

  function isiUlangOtomatis() {
    bersihkanPesan();
    setBaris(susunBarisOtomatis(subs));
    setPesan("Tabel diisi ulang dari data Progress Kegiatan. Suntingan sebelumnya pada tabel tergantikan.");
  }

  function ubahBaris(i, kunci, nilai) {
    setBaris((prev) => prev.map((b, idx) => (idx === i ? { ...b, [kunci]: nilai } : b)));
  }

  function hapusBaris(i) {
    setBaris((prev) => prev.filter((_, idx) => idx !== i).map((b, idx) => ({ ...b, no: String(idx + 1) })));
  }

  function tambahBaris() {
    setBaris((prev) => [...prev, { no: String(prev.length + 1), uraian: "", progres: "" }]);
  }

  async function unduh(format) {
    bersihkanPesan();
    if (!baris.length) {
      setError("Tabel masih kosong. Isi minimal satu baris pekerjaan.");
      return;
    }
    setSibuk(format);
    try {
      const { data } = await api.post(`/laporan-progres/${format}`, isiLaporan, { responseType: "blob" });
      const bersih = tanggal.replace(/[^A-Za-z0-9]+/g, "-");
      unduhBlob(data, `Rekapitulasi-Progres-Irbansus-${bersih}.${format}`);
      setPesan(format === "pdf" ? "Berkas PDF terunduh." : "Berkas Word terunduh dan siap disunting.");
    } catch (e) {
      // respons error ikut berbentuk blob karena responseType di atas
      let detail = e?.response?.data;
      if (detail instanceof Blob) {
        try { detail = JSON.parse(await detail.text())?.detail; } catch { detail = null; }
      }
      setError(formatApiError(detail) || "Gagal membuat berkas laporan.");
    } finally {
      setSibuk(null);
    }
  }

  async function simpanNaskah() {
    bersihkanPesan();
    setSibuk("simpan");
    try {
      const { data } = await api.put("/laporan-progres/draft", isiLaporan);
      setPesan(data?.message || "Naskah laporan tersimpan.");
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Gagal menyimpan naskah.");
    } finally {
      setSibuk(null);
    }
  }

  async function kirimWA() {
    bersihkanPesan();
    setSibuk("wa");
    try {
      const { data: users } = await api.get("/users");
      const kandidat = (Array.isArray(users) ? users : []).filter((u) => u.role === "IRBAN");
      if (!kandidat.length) throw new Error("Tidak ada pengguna berperan IRBAN pada daftar tim.");
      const irban =
        kandidat.find((u) => (u.name || "").toLowerCase().includes("ara sundara")) || kandidat[0];
      const nomor = normalizeWANumberLocal(irban.noWA);
      if (!/^628\d{7,11}$/.test(nomor)) {
        throw new Error(`Nomor WA ${irban.name} belum benar. Perbaiki di menu Pengaturan.`);
      }

      const teks = [
        "Yth. Pak Irban Khusus",
        "",
        `Ijin menyampaikan rekapitulasi progres pekerjaan Irban Khusus pada Inspektorat Daerah Kabupaten Tasikmalaya per ${tanggal}.`,
        "",
        `Jumlah pekerjaan yang dilaporkan: ${baris.length}`,
        "",
        "Laporan lengkap terlampir dalam berkas Word dan PDF.",
        "",
        "Demikian laporan ini kami sampaikan sebagai bahan lebih lanjut.",
        "",
        "Penanggung jawab kegiatan,",
        user?.name || "",
      ].join("\n");

      window.open(`https://wa.me/${nomor}?text=${encodeURIComponent(teks)}`, "_blank", "noopener,noreferrer");
      setPesan(
        `WhatsApp dibuka untuk ${irban.name}. Pesan belum terkirim — unduh dulu berkas laporannya, lampirkan di chat, baru tekan kirim.`
      );
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message || "Gagal menyiapkan pesan WhatsApp.");
    } finally {
      setSibuk(null);
    }
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-indigo-100/70 bg-white">
      {/* Kepala panel */}
      <button
        onClick={() => setTerbuka((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-indigo-50/40"
      >
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl text-white"
            style={{ background: "linear-gradient(135deg, #4338ca 0%, #6366f1 100%)" }}
          >
            <FileText className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <div className="text-[13.5px] font-bold text-slate-800">Laporan Rekapitulasi Progres</div>
            <div className="mt-0.5 text-[11.5px] text-slate-500">
              {baris.length} pekerjaan · {jumlahKata} kata · format resmi Irbansus
            </div>
          </div>
        </div>
        <span className="flex items-center gap-1 text-[11.5px] font-semibold text-indigo-600">
          {terbuka ? "Tutup" : "Buka"}
          {terbuka ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>

      {terbuka && (
        <div className="border-t border-slate-100 px-5 py-4">
          {/* Baris tombol */}
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Tanggal laporan
              </span>
              <input
                value={tanggal}
                onChange={(e) => setTanggal(e.target.value)}
                placeholder="12 Juli 2026"
                className="w-44 rounded-lg border border-slate-200 px-3 py-2 text-[12.5px] focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </label>

            <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
              <button
                onClick={isiUlangOtomatis}
                title="Susun ulang tabel dari data Progress Kegiatan"
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] font-semibold text-slate-600 transition-colors hover:bg-slate-50"
              >
                <Wand2 className="h-3.5 w-3.5" strokeWidth={2} />
                Isi otomatis
              </button>
              {bolehUbah && (
                <button
                  onClick={simpanNaskah}
                  disabled={sibuk === "simpan"}
                  className="flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-[12px] font-semibold text-indigo-600 transition-colors hover:bg-indigo-50 disabled:opacity-60"
                >
                  {sibuk === "simpan" ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" strokeWidth={2} />}
                  Simpan naskah
                </button>
              )}
              <button
                onClick={() => unduh("docx")}
                disabled={sibuk === "docx"}
                className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-[12px] font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
              >
                {sibuk === "docx" ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" strokeWidth={2.2} />}
                Unduh Word
              </button>
              <button
                onClick={() => unduh("pdf")}
                disabled={sibuk === "pdf"}
                className="flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-2 text-[12px] font-bold text-white transition-colors hover:bg-rose-700 disabled:opacity-60"
              >
                {sibuk === "pdf" ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" strokeWidth={2.2} />}
                Unduh PDF
              </button>
              <button
                onClick={kirimWA}
                disabled={sibuk === "wa"}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-[12px] font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60"
              >
                {sibuk === "wa" ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" strokeWidth={2.2} />}
                Kirim WA
              </button>
            </div>
          </div>

          {pesan && (
            <div className="mb-3 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11.5px] text-emerald-700">
              <CheckCircle2 className="mt-px h-4 w-4 flex-shrink-0" strokeWidth={2} />
              <span className="break-words">{pesan}</span>
            </div>
          )}
          {error && (
            <div className="mb-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] text-red-700">
              <AlertTriangle className="mt-px h-4 w-4 flex-shrink-0" strokeWidth={2} />
              <span className="break-words">{error}</span>
            </div>
          )}

          <div className="mb-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
            <Info className="mt-px h-3.5 w-3.5 flex-shrink-0 text-sky-500" strokeWidth={2} />
            <span>
              Kop surat, jenis huruf, indentasi, dan pengaturan paragraf mengikuti berkas contoh
              resmi — tidak perlu diatur di sini. Yang kamu sunting hanya isinya.
            </span>
          </div>

          {/* Tabel rekap */}
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <div className="flex bg-slate-50 text-[10.5px] font-bold uppercase tracking-wider text-slate-500">
              <div className="w-12 px-2 py-2 text-center">No</div>
              <div className="w-64 border-l border-slate-200 px-2 py-2">Uraian Pekerjaan</div>
              <div className="flex-1 border-l border-slate-200 px-2 py-2">Progres</div>
              <div className="w-10 border-l border-slate-200" />
            </div>
            {baris.length === 0 ? (
              <div className="px-4 py-8 text-center text-[12px] text-slate-400">
                Belum ada baris. Klik "Isi otomatis" atau tambah sendiri di bawah.
              </div>
            ) : (
              baris.map((b, i) => (
                <div key={i} className="flex border-t border-slate-100">
                  <div className="w-12 px-2 py-2 text-center text-[11.5px] font-semibold text-slate-500">
                    {i + 1}
                  </div>
                  <div className="w-64 border-l border-slate-100 p-1.5">
                    <textarea
                      rows={3}
                      value={b.uraian}
                      onChange={(e) => ubahBaris(i, "uraian", e.target.value)}
                      className="w-full resize-y rounded-md border border-transparent px-2 py-1 text-[11.5px] leading-snug text-slate-800 hover:border-slate-200 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    />
                  </div>
                  <div className="flex-1 border-l border-slate-100 p-1.5">
                    <textarea
                      rows={3}
                      value={b.progres}
                      onChange={(e) => ubahBaris(i, "progres", e.target.value)}
                      className="w-full resize-y rounded-md border border-transparent px-2 py-1 text-[11.5px] leading-snug text-slate-700 hover:border-slate-200 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    />
                  </div>
                  <div className="flex w-10 items-start justify-center border-l border-slate-100 pt-2">
                    <button
                      onClick={() => hapusBaris(i)}
                      title="Hapus baris"
                      className="rounded-md p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <button
            onClick={tambahBaris}
            className="mt-2 flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-[11.5px] font-semibold text-slate-500 transition-colors hover:border-indigo-300 hover:text-indigo-600"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2} />
            Tambah baris
          </button>

          {/* Lampiran */}
          <div className="mt-5">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="text-[12.5px] font-bold text-slate-800">Lampiran (opsional)</div>
                <div className="text-[11px] text-slate-500">
                  Penjelasan naratif per pekerjaan. Dikosongkan berarti laporan hanya satu halaman rekap.
                </div>
              </div>
              <button
                onClick={() => setLampiran((p) => [...p, { judul: "", narasi: "" }])}
                className="flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-[11.5px] font-semibold text-slate-500 transition-colors hover:border-indigo-300 hover:text-indigo-600"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                Tambah butir
              </button>
            </div>

            {lampiran.map((l, i) => (
              <div key={i} className="mb-2 rounded-xl border border-slate-200 p-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-400">{i + 1}.</span>
                  <input
                    value={l.judul}
                    onChange={(e) =>
                      setLampiran((p) => p.map((x, idx) => (idx === i ? { ...x, judul: e.target.value } : x)))
                    }
                    placeholder="Judul butir lampiran"
                    className="flex-1 rounded-md border border-slate-200 px-2 py-1.5 text-[12px] font-semibold text-slate-800 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                  />
                  <button
                    onClick={() => setLampiran((p) => p.filter((_, idx) => idx !== i))}
                    title="Hapus butir"
                    className="rounded-md p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                </div>
                <textarea
                  rows={3}
                  value={l.narasi}
                  onChange={(e) =>
                    setLampiran((p) => p.map((x, idx) => (idx === i ? { ...x, narasi: e.target.value } : x)))
                  }
                  placeholder="Uraian penjelasan…"
                  className="mt-2 w-full resize-y rounded-md border border-slate-200 px-2 py-1.5 text-[11.5px] leading-relaxed text-slate-700 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
